import { useState, useContext } from "react";
import {
  Box,
  Typography,
  TextField,
  Select,
  MenuItem,
  Checkbox,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Button,
  Stack,
  FormControl,
  InputLabel,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import { LookupValueContext } from "../../common/contexts/LookupValueContext";
import { saveMasterCodeLegendEntry } from "../../api/APIHelpers";
import { AlertsContext } from "../../common/contexts/AlertsContext";
import { CodeLegendContext } from "../../common/contexts/CodeLegendContext";
import { extractApiErrorMessage } from "../../common/helpers/ErrorUtils";

const MasterEntryEditor = ({ entry }) => {
  const [masterEntry, setMasterEntry] = useState(entry);
  const [altDescs, setAltDescs] = useState(entry.AltDescriptions || []);
  const [saving, setSaving] = useState(false);
  const lookupValues = useContext(LookupValueContext);
  const { setInfoMessages } = useContext(AlertsContext);
  const { refreshCodeLegendContext } = useContext(CodeLegendContext);

  const alarmStateObj = lookupValues?.find(
    (lv) => lv.LookupName === "AlarmState"
  );
  const alarmStates =
    alarmStateObj?.Values?.map((value) => ({
      id: value.LookupValueId,
      name: value.DisplayName,
    })) || [];
  const isMechObj = lookupValues?.find((lv) => lv.LookupName === "IsMech");
  const isMechOptions =
    isMechObj?.Values?.map((value) => ({
      id: value.LookupValueId,
      name: value.DisplayName,
    })) || [];

  // Master entry field change
  const handleFieldChange = (field, value) => {
    setMasterEntry((prev) => ({ ...prev, [field]: value }));
  };

  // AltDesc table handlers
  const handleAltDescChange = (idx, value) => {
    setAltDescs((prev) => {
      const newDescs = [...prev];
      newDescs[idx] = { ...newDescs[idx], Description: value };
      return newDescs;
    });
  };

  const handleDeleteAltDesc = (idx) => {
    setAltDescs((prev) => prev.filter((_, i) => i !== idx));
  };

  const handleAddAltDesc = () => {
    setAltDescs((prev) => [
      ...prev,
      {
        CodeLegendMasterEntryAltDescId: 0,
        CodeLegendMasterEntryId: masterEntry.CodeLegendMasterEntryId,
        Description: "",
      },
    ]);
  };

  const handleSaveChanges = async () => {
    setSaving(true);
    try {
      const fullRecord = { ...masterEntry, AltDescriptions: altDescs };
      await saveMasterCodeLegendEntry(fullRecord);
      await refreshCodeLegendContext();
      setInfoMessages({
        type: "snackbar",
        message: "Standard Code Description saved successfully.",
        severity: "success",
        open: true,
      });
    } catch (err) {
      setInfoMessages({
        type: "snackbar",
        message: `Error saving Standard Code Description: ${extractApiErrorMessage(err)}`,
        severity: "error",
        open: true,
      });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Box sx={{ p: 2 }}>
      <Stack spacing={2} sx={{ mb: 3 }}>
        <TextField
          label='Description'
          value={masterEntry.Description || ""}
          onChange={(e) => handleFieldChange("Description", e.target.value)}
          fullWidth
          disabled={saving}
        />
        <Box sx={{ display: "flex", gap: 2 }}>
          <FormControl>
            <InputLabel>Alarm State</InputLabel>
            <Select
              label='Alarm State'
              value={masterEntry.AlarmStateId ?? ""}
              onChange={(e) =>
                handleFieldChange("AlarmStateId", e.target.value)
              }
              sx={{ minWidth: 180 }}
              disabled={saving}>
              {alarmStates.map((state) => (
                <MenuItem key={state.id} value={state.id}>
                  {state.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl>
            <InputLabel>Is Mechanical</InputLabel>
            <Select
              label='Is Mechanical'
              value={masterEntry.IsMechId ?? ""}
              onChange={(e) => handleFieldChange("IsMechId", e.target.value)}
              sx={{ minWidth: 180 }}
              disabled={saving}>
              {isMechOptions.map((option) => (
                <MenuItem key={option.id} value={option.id}>
                  {option.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <Stack direction='row' alignItems='center' spacing={1}>
            <Checkbox
              checked={!!masterEntry.SendAlerts}
              onChange={(e) =>
                handleFieldChange("SendAlerts", e.target.checked)
              }
              disabled={saving}
            />
            <Typography>Send Alerts</Typography>
          </Stack>
        </Box>
      </Stack>
      <Typography variant='h6' gutterBottom>
        Alternate Descriptions
      </Typography>
      <TableContainer component={Paper} sx={{ maxHeight: 250, mb: 2 }}>
        <Table size='small'>
          <TableHead>
            <TableRow>
              <TableCell>Description</TableCell>
              <TableCell>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {altDescs.map((desc, idx) => (
              <TableRow key={idx}>
                <TableCell>
                  <TextField
                    value={desc.Description || ""}
                    onChange={(e) => handleAltDescChange(idx, e.target.value)}
                    fullWidth
                    size='small'
                    disabled={saving}
                  />
                </TableCell>
                <TableCell>
                  <IconButton
                    aria-label='delete-altdesc'
                    color='error'
                    onClick={() => handleDeleteAltDesc(idx)}
                    disabled={saving}>
                    <DeleteIcon />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
            {altDescs.length === 0 && (
              <TableRow>
                <TableCell colSpan={2} align='center'>
                  No alternate descriptions.
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
      <Box sx={{ display: "flex", justifyContent: "space-between" }}>
        <Button variant='outlined' onClick={handleAddAltDesc} disabled={saving}>
          Add Alternate Description
        </Button>
        <Button
          variant='contained'
          color='primary'
          onClick={handleSaveChanges}
          disabled={saving}>
          {saving ? "Saving..." : "Save Changes"}
        </Button>
      </Box>
    </Box>
  );
};

export default MasterEntryEditor;
